import React from 'react';
import { BASEURL } from '@services/network';
import { logError } from '@utils';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logger } from '@utils/usefulFuncs';
import {
  createAnswersLookupFromPayload,
  generateTemplateKeyLookup,
  parseRfpStreamMessage,
} from './rfpTemplateAnswerUtils';

const ACTIONS = {
  reset: 'reset',
  init: 'init',
  update: 'update',
  error: 'error',
  complete: 'complete',
};

const initialState = {
  answersLookup: {},
  keyLookup: {},
  streamError: null,
  streamEnd: false,
};

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.reset: {
      return initialState;
    }
    case ACTIONS.init: {
      return {
        answersLookup: {},
        keyLookup: generateTemplateKeyLookup(action.payload.queryListMetadata),
        streamError: null,
        streamEnd: false,
      };
    }
    case ACTIONS.update: {
      const { id, message } = parseRfpStreamMessage(action.payload);
      const templateKey = state.keyLookup[id];
      if (!templateKey) {
        return state;
      }

      return {
        ...state,
        streamError: null,
        answersLookup: {
          ...state.answersLookup,
          [templateKey]: message,
        },
      };
    }
    case ACTIONS.error: {
      return {
        ...state,
        streamError: action.payload,
      };
    }
    case ACTIONS.complete: {
      const completedAnswersLookup = createAnswersLookupFromPayload(action.payload, state.keyLookup);
      return {
        ...state,
        answersLookup: {
          ...state.answersLookup,
          ...completedAnswersLookup,
        },
        streamError: null,
        streamEnd: true,
      };
    }
    default: {
      throw new Error('Missing case');
    }
  }
}

function openStreamingResponse(onCloseSource, streamingId, dispatch) {
  return new Promise((resolve, reject) => {
    if (!streamingId) {
      reject(new Error('streamingId was missing'));
      return;
    }

    const url = `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(streamingId)}&include_context=true`;
    let source = createAuthedEventSource(url);

    onCloseSource.call = () => {
      console.log('[RFP] useRfpTemplateAnswers:close source', {
        streamingId,
      });
      source.close();
    };

    const onOpen = () => {
      console.log('[RFP] useRfpTemplateAnswers:onOpen', {
        streamingId,
      });
    };

    const onMessage = (event) => {
      try {
        let parsed = null;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          // Stream events can be non-JSON while the UI still appends the raw payload below.
        }
        logger('useRfpTemplateAnswers onMessage', 'received', {
          runId: streamingId,
          questionIndex: parsed?.question_index,
          finalAnswer: parsed?.final_answer === true,
          answerLength: parsed?.answer?.length || 0,
        });
        dispatch({
          type: ACTIONS.update,
          payload: event.data,
        });
      } catch (error) {
        logError(error, {
          component: 'useRfpTemplateAnswers',
          message: 'Unable to parse RFP answer stream update.',
        });
      }
    };

    const onError = (error) => {
      console.log('[RFP] useRfpTemplateAnswers:onError', {
        streamingId,
        error,
      });
      source.removeEventListener('open', onOpen);
      source.removeEventListener('error', onError);
      source.removeEventListener('message', onMessage);
      source.removeEventListener('complete', onComplete);
      source.close();
      reject(error);
    };

    const onComplete = (event) => {
      try {
        console.log('[RFP] useRfpTemplateAnswers:onComplete raw', {
          streamingId,
          rawData: event.data,
        });
        const streamData = JSON.parse(event.data);
        const answers = streamData?.response?.answers || [];

        dispatch({
          type: ACTIONS.complete,
          payload: answers,
        });

        source.removeEventListener('open', onOpen);
        source.removeEventListener('error', onError);
        source.removeEventListener('message', onMessage);
        source.removeEventListener('complete', onComplete);
        source.close();
        resolve('complete');
      } catch (error) {
        source.close();
        reject(error);
      }
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('message', onMessage);
    source.addEventListener('complete', onComplete);
  });
}

export function useRfpTemplateAnswers(streamingId, queryListMetadata) {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  React.useEffect(() => {
    if (queryListMetadata?.length > 0) {
      dispatch({
        type: ACTIONS.init,
        payload: { queryListMetadata },
      });
      return;
    }

    dispatch({ type: ACTIONS.reset });
  }, [queryListMetadata]);

  React.useEffect(() => {
    if (!streamingId || !queryListMetadata?.length) {
      return () => {};
    }

    const activeSources = [];
    const maxRetries = 2;
    const retryDelayMS = 1000;
    let isMounted = true;

    const connectToStream = (retriesLeft) => {
      const onCloseSource = {};
      activeSources.push(onCloseSource);

      openStreamingResponse(onCloseSource, streamingId, dispatch).catch((error) => {
        if (!isMounted) {
          return;
        }

        if (retriesLeft > 0) {
          window.setTimeout(() => {
            connectToStream(retriesLeft - 1);
          }, retryDelayMS);
          return;
        }

        logError(error, {
          component: 'useRfpTemplateAnswers',
          message: 'Unable to stream RFP template answers.',
        });

        dispatch({
          type: ACTIONS.error,
          payload: 'Unable to load live answers. You can still download the completed report.',
        });
      });
    };

    connectToStream(maxRetries);

    return () => {
      isMounted = false;
      activeSources.forEach((activeSource) => {
        activeSource?.call && activeSource.call();
      });
      dispatch({ type: ACTIONS.reset });
    };
  }, [queryListMetadata, streamingId]);

  return state;
}
